/**
 * Sync list-page filters to the URL query string so views can be shared/bookmarked.
 *
 * Implementing pages should define:
 *
 *   urlQueryFilterConfig() {
 *     return {
 *       search: { path: 'tables.orders.filter', query: 'q' },
 *       page: { path: 'tables.orders.currentPage', query: 'page', type: 'number' },
 *       perPage: { path: 'tables.orders.perPage', query: 'perPage', type: 'number' },
 *       sortBy: { path: 'tables.orders.sortBy', query: 'sortBy' },
 *       sortDesc: { path: 'tables.orders.sortDesc', query: 'sortDesc', type: 'boolean' },
 *       fields: [
 *         { path: 'filters.selected.status', query: 'status', type: 'array' },
 *         { path: 'filters.selected.dateFrom', query: 'from' },
 *       ],
 *     }
 *   }
 *
 * Call applyUrlQueryFilters() during init (after remembered state) so URL wins when present.
 * Call syncUrlQueryFilters() whenever filters/search/paging/sort change.
 */
function getByPath(obj, path) {
    if (!path) {
        return undefined
    }

    return path.split('.').reduce((current, key) => {
        if (current == null) {
            return undefined
        }

        return current[key]
    }, obj)
}

function setByPath(vm, path, value) {
    if (!path) {
        return
    }

    const keys = path.split('.')
    const last = keys.pop()
    let target = vm

    keys.forEach((key) => {
        if (target[key] == null) {
            vm.$set(target, key, {})
        }
        target = target[key]
    })

    vm.$set(target, last, value)
}

function valuesEqual(a, b) {
    if (a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b)) {
        return JSON.stringify(a) === JSON.stringify(b)
    }

    if (Array.isArray(a) || Array.isArray(b)) {
        const left = Array.isArray(a) ? a.map(String) : []
        const right = Array.isArray(b) ? b.map(String) : []
        if (left.length !== right.length) {
            return false
        }
        return left.every((value, index) => value === right[index])
    }

    return String(a ?? '') === String(b ?? '')
}

function normalizeQueryValue(value, type) {
    if (type === 'json') {
        if (value == null || value === '') {
            return {}
        }
        if (typeof value === 'object' && !Array.isArray(value)) {
            return value
        }
        try {
            const parsed = JSON.parse(String(value))
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
        } catch (e) {
            return {}
        }
    }

    if (type === 'array') {
        if (Array.isArray(value)) {
            return value.filter((item) => item !== '' && item != null).map((item) => coerceScalar(item))
        }

        if (value == null || value === '') {
            return []
        }

        return String(value)
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
            .map((item) => coerceScalar(item))
    }

    if (type === 'number') {
        if (value == null || value === '') {
            return null
        }

        const number = Number(value)
        return Number.isNaN(number) ? null : number
    }

    if (type === 'boolean') {
        if (value === true || value === 'true' || value === '1' || value === 1) {
            return true
        }
        if (value === false || value === 'false' || value === '0' || value === 0) {
            return false
        }
        return null
    }

    if (value == null) {
        return ''
    }

    return coerceScalar(value)
}

function coerceScalar(value) {
    if (typeof value === 'number' || typeof value === 'boolean') {
        return value
    }

    const asString = String(value)
    if (asString !== '' && !Number.isNaN(Number(asString)) && /^-?\d+(\.\d+)?$/.test(asString)) {
        return Number(asString)
    }

    return asString
}

function serializeQueryValue(value, type) {
    if (type === 'json') {
        if (!value || typeof value !== 'object' || Array.isArray(value) || !Object.keys(value).length) {
            return null
        }
        return JSON.stringify(value)
    }

    if (type === 'array') {
        const list = Array.isArray(value) ? value : []
        return list.length ? list.join(',') : null
    }

    if (type === 'boolean') {
        if (value === true) return '1'
        if (value === false) return '0'
        return null
    }

    if (value == null || value === '') {
        return null
    }

    return String(value)
}

function queriesMatch(nextQuery, currentQuery, keys) {
    return keys.every((key) => String(nextQuery[key] ?? '') === String(currentQuery[key] ?? ''))
}

export default {
    data() {
        return {
            urlQueryFiltersSyncing: false,
            urlQueryFiltersReady: false,
        }
    },
    methods: {
        getUrlQueryFilterConfig() {
            if (typeof this.urlQueryFilterConfig !== 'function') {
                return null
            }

            return this.urlQueryFilterConfig()
        },
            getUrlQueryFilterEntries() {
            const config = this.getUrlQueryFilterConfig()
            if (!config) {
                return []
            }

            const entries = []

            if (config.search) {
                entries.push({ ...config.search, type: config.search.type || 'string' })
            }
            if (config.page) {
                entries.push({ ...config.page, type: config.page.type || 'number', resetWhenMissing: false })
            }
            if (config.perPage) {
                entries.push({ ...config.perPage, type: config.perPage.type || 'number', resetWhenMissing: false })
            }
            if (config.sortBy) {
                entries.push({ ...config.sortBy, type: config.sortBy.type || 'string', resetWhenMissing: false })
            }
            if (config.sortDesc) {
                entries.push({ ...config.sortDesc, type: config.sortDesc.type || 'boolean', resetWhenMissing: false })
            }
            if (Array.isArray(config.fields)) {
                config.fields.forEach((field) => {
                    entries.push({
                        ...field,
                        type: field.type || 'string',
                        resetWhenMissing: field.resetWhenMissing !== false,
                    })
                })
            }

            return entries.filter((entry) => entry.path && entry.query)
        },
        routeHasUrlQueryFilters() {
            const query = this.$route.query || {}
            return this.getUrlQueryFilterEntries().some((entry) => {
                const value = query[entry.query]
                return value != null && value !== ''
            })
        },
        buildUrlQueryFromFilters() {
            const query = {}

            this.getUrlQueryFilterEntries().forEach((entry) => {
                const raw = getByPath(this, entry.path)
                if (Object.prototype.hasOwnProperty.call(entry, 'emptyValue') && valuesEqual(raw, entry.emptyValue)) {
                    return
                }

                const serialized = serializeQueryValue(raw, entry.type)
                if (serialized != null) {
                    query[entry.query] = serialized
                }
            })

            return query
        },
        applyUrlQueryFilters() {
            const config = this.getUrlQueryFilterConfig()
            if (!config || !this.routeHasUrlQueryFilters()) {
                return false
            }

            this.urlQueryFiltersSyncing = true
            const query = this.$route.query || {}
            let changed = false

            this.getUrlQueryFilterEntries().forEach((entry) => {
                const hasKey = Object.prototype.hasOwnProperty.call(query, entry.query)
                    && query[entry.query] !== ''
                    && query[entry.query] != null

                if (!hasKey) {
                    // Filter fields become empty when omitted from a shareable URL.
                    // Paging/sort keep whatever was already initialized (remembered defaults).
                    if (entry.resetWhenMissing === false) {
                        return
                    }

                    if (entry.type === 'array') {
                        const current = getByPath(this, entry.path)
                        if (Array.isArray(current) && current.length) {
                            setByPath(this, entry.path, [])
                            changed = true
                        }
                        return
                    }

                    if (entry.type === 'json') {
                        const emptyValue = entry.emptyValue != null ? entry.emptyValue : {}
                        const current = getByPath(this, entry.path)
                        if (!valuesEqual(current, emptyValue)) {
                            setByPath(this, entry.path, emptyValue)
                            changed = true
                        }
                        return
                    }

                    if (entry.type === 'boolean') {
                        return
                    }

                    const emptyValue = entry.type === 'number' ? null : (entry.emptyValue != null ? entry.emptyValue : '')
                    const current = getByPath(this, entry.path)
                    if (current != null && current !== emptyValue && current !== '') {
                        setByPath(this, entry.path, emptyValue)
                        changed = true
                    }
                    return
                }

                const nextValue = normalizeQueryValue(query[entry.query], entry.type)
                if (nextValue == null && entry.type === 'number') {
                    return
                }

                const current = getByPath(this, entry.path)
                if (!valuesEqual(current, nextValue)) {
                    setByPath(this, entry.path, nextValue)
                    changed = true
                }
            })

            this.$nextTick(() => {
                this.urlQueryFiltersSyncing = false
                if (typeof this.onUrlQueryFiltersApplied === 'function') {
                    this.onUrlQueryFiltersApplied(changed)
                }
            })

            return true
        },
        syncUrlQueryFilters() {
            if (this.urlQueryFiltersSyncing || !this.urlQueryFiltersReady) {
                return
            }

            const config = this.getUrlQueryFilterConfig()
            if (!config) {
                return
            }

            const nextQuery = this.buildUrlQueryFromFilters()
            const keys = new Set([
                ...Object.keys(nextQuery),
                ...this.getUrlQueryFilterEntries().map((entry) => entry.query),
            ])

            if (queriesMatch(nextQuery, this.$route.query || {}, Array.from(keys))) {
                return
            }

            this.urlQueryFiltersSyncing = true
            this.$router.replace({
                name: this.$route.name,
                params: this.$route.params,
                query: nextQuery,
            }).catch(() => {
                // Ignore NavigationDuplicated
            }).finally(() => {
                this.$nextTick(() => {
                    this.urlQueryFiltersSyncing = false
                })
            })
        },
        enableUrlQueryFilters() {
            this.urlQueryFiltersReady = true
            this.syncUrlQueryFilters()
        },
    },
    watch: {
        '$route.query'(to, from) {
            if (!this.urlQueryFiltersReady || this.urlQueryFiltersSyncing) {
                return
            }

            if (!this.getUrlQueryFilterConfig()) {
                return
            }

            // Browser back/forward or shared-link navigation
            if (JSON.stringify(to || {}) === JSON.stringify(from || {})) {
                return
            }

            this.applyUrlQueryFilters()
        },
    },
}
